'use-client';
import * as React from 'react';
import type { SliderProps as WSRSliderProps } from 'wix-style-react';
import { getDataAttributes } from '@wix/editor-elements-common-utils';
import classNames from 'clsx';
import type {
  IRangeSliderProps,
  IRangeSliderImperativeActions,
  SliderType,
} from '../RangeSlider.types';

import { TestIds } from '../constants';
import SteppedSlider from './SteppedSlider/SteppedSlider';
import ContinuousSlider from './ContinuousSlider/ContinuousSlider';
import { st, classes } from './style/RangeSlider.component.st.css';
import { debounce } from 'lodash';

const SliderToRender: Record<SliderType, React.FC<any>> = {
  Continuous: ContinuousSlider,
  Stepped: SteppedSlider,
};

const toSliderValue = (
  minValue: number,
  maxValue: number,
): WSRSliderProps['value'] => {
  if (minValue !== maxValue) {
    return [minValue, maxValue];
  }
  return minValue;
};

const RangeSlider: React.ForwardRefRenderFunction<
  IRangeSliderImperativeActions,
  IRangeSliderProps
> = (props, ref) => {
  const {
    id,
    className,
    stylableClassName,
    resolvedSliderDirection,
    label,
    sliderType,
    min,
    max,
    stepType,
    step,
    minValue,
    maxValue,
    prefix,
    suffix,
    marksTickShape,
    isDisabled,
    width,
    readOnly,
    onChange,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    updateComponentPropsInViewer,
    translations,
    lang,
    accessibleName,
  } = props;

  const rtl = resolvedSliderDirection === 'rtl';

  const singleHandle = minValue === maxValue;

  const Slider = SliderToRender[sliderType];

  const stepValue = stepType === 'Number' ? (max - min) / step : step;

  const rangeRef = React.useRef<HTMLDivElement>(null);

  const value = React.useMemo(
    () => toSliderValue(minValue, maxValue),
    [minValue, maxValue],
  );

  const ignoreNextChange = React.useRef(false);

  const skipNextChange = React.useCallback(() => {
    ignoreNextChange.current = true;
  }, []);

  const unskipNextChange = React.useCallback(() => {
    ignoreNextChange.current = false;
  }, []);

  const debouncedUnskipNextChange = React.useMemo(
    () => debounce(unskipNextChange, 150),
    [unskipNextChange],
  );

  const handleChange = (newValue: WSRSliderProps['value']) => {
    if (ignoreNextChange.current) {
      ignoreNextChange.current = false;
      debouncedUnskipNextChange.cancel();
      return;
    }
    if (readOnly) {
      return;
    }
    if (typeof newValue === 'number') {
      updateComponentPropsInViewer({ minValue: newValue, maxValue: newValue });
      onChange?.({ type: 'change' } as React.ChangeEvent);
    } else {
      const [newMinValue, newMaxValue] = newValue
        ? newValue
        : [minValue, maxValue];
      updateComponentPropsInViewer({
        minValue: newMinValue,
        maxValue: newMaxValue,
      });
      onChange?.({ type: 'change' } as React.ChangeEvent);
    }
  };

  React.useImperativeHandle(ref, () => {
    return {
      focus: () => {
        rangeRef.current?.focus();
      },
      blur: () => {
        rangeRef.current?.blur();
      },
    };
  });

  const handleFocus: WSRSliderProps['onFocus'] = ((
    _,
    event: React.FocusEvent<Element>,
  ) => {
    onFocus?.(event);
    skipNextChange();
    debouncedUnskipNextChange();
  }) as WSRSliderProps['onFocus'];

  React.useEffect(
    () => () => {
      debouncedUnskipNextChange.cancel();
    },
    [debouncedUnskipNextChange],
  );

  return (
    <div
      id={id}
      className={classNames(className, classes.container)}
      {...getDataAttributes(props)}
      lang={lang}
    >
      <div
        className={st(
          classes.root,
          { sliderType, marksTickShape, singleHandle },
          stylableClassName,
        )}
        style={
          {
            '--MinThumbPos': (minValue - min) / (max - min),
            '--MaxThumbPos': (maxValue - min) / (max - min),
          } as React.CSSProperties
        }
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {label && <label className={classes.label}>{label}</label>}
        <div id={`accessible-name-${id}`} style={{ display: 'none' }}>
          {accessibleName || label}
        </div>
        <div
          id={`min-value-div-${id}`}
          style={{ display: 'none' }}
        >{`${translations.minValueAriaLabel}`}</div>
        <div
          id={`max-value-div-${id}`}
          style={{ display: 'none' }}
        >{`${translations.maxValueAriaLabel}`}</div>
        <Slider
          className={classes.baseSlider}
          dataHook={TestIds.baseSlider}
          id={`slider-${id}`}
          rtl={rtl}
          min={min}
          max={max}
          value={value}
          step={stepValue}
          prefix={prefix}
          suffix={suffix}
          onChange={handleChange}
          disabled={isDisabled}
          width={width}
          pushable={true}
          ariaLabelledByForHandle={[
            `accessible-name-${id} min-value-div-${id}`,
            `accessible-name-${id} max-value-div-${id}`,
          ]}
          onFocus={handleFocus}
          onBlur={((_, e) => onBlur?.(e!)) as WSRSliderProps['onBlur']}
          rangeRef={rangeRef}
        />
      </div>
    </div>
  );
};

export default React.forwardRef(RangeSlider);
